import { computed, ComputedRef } from 'vue';

import { ExchAppActions, ExchAppState } from '@/exch-app/store/exch-app-store-types';
import { OrgAppActions, OrgAppGetters, OrgAppState } from '@/org-app/store/org-app-store-types';
import rootStore from '@/store';
import { Getters, StoreInternals } from '@/store/store-types';
import { MappedPostAction } from '@/utils/ActionsUtils';

const ORG_APP_PREFIX = 'orgApp/';
const EXCH_APP_PREFIX = 'exchApp/';

// #region Utility Types

/**
 * Archetype for a store action.
 */
type StoreAction<Config> = ({ commit }, args: Config) => Promise<unknown> | unknown;

/**
 * Strips the first argument from a function.
 */
type OmitFirstArg<F> = F extends (firstArg, ...args: infer Rest) => infer R ? (...args: Rest) => R : unknown;

/**
 * Archetype for a post-action configuration.
 */
type PostActionConfig<T> = { instances: T | T[] };

type IsAny<T> = 0 extends 1 & T ? true : false;

/**
 * Maps a store action to a function that takes only the action's arguments.
 */
type MappedAction<Action> =
  Action extends StoreAction<infer Config>
    ? IsAny<Config> extends true // If the Config type cannot be determined, do not attempt to read the instances type
      ? OmitFirstArg<Action>
      : Config extends PostActionConfig<infer T> // Is this a post-action configuration (ie: does it contain an `instances` property) ?
        ? MappedPostAction<T> // If so, return the MapPostAction type that properly supports instances types, otherwise typescript doesn't know if the action should return an array or a single instance
        : OmitFirstArg<Action>
    : unknown;

// #endregion

function getStore() {
  return rootStore as StoreInternals;
}

type MappedState = {
  [K in keyof StoreInternals['state']]: ComputedRef<StoreInternals['state'][K]>;
};

function mapState(): MappedState {
  const store = getStore();
  return Object.fromEntries(
    Object.keys(store.state).map((key) => [key, computed(() => store.state[key])]),
  ) as unknown as MappedState;
}

function mapOrgAppState(): OrgAppState {
  const store = getStore();
  return store.state.orgApp;
}

function mapExchAppState(): ExchAppState {
  const store = getStore();
  return store.state.exchApp;
}

type MappedGetters = {
  [K in keyof Getters]: ComputedRef<ReturnType<Getters[K]>>;
};

function mapGetters(): MappedGetters {
  const store = getStore();
  return Object.fromEntries(
    Object.keys(store.getters).map((getter) => [getter, computed(() => store.getters[getter])]),
  ) as unknown as MappedGetters;
}

type MappedOrgAppGetters = {
  [K in keyof OrgAppGetters]: ComputedRef<ReturnType<OrgAppGetters[K]>>;
};

function mapOrgAppGetters(): MappedOrgAppGetters {
  const store = getStore();
  return Object.fromEntries(
    Object.keys(store.getters)
      .filter((x) => x.startsWith(ORG_APP_PREFIX))
      .map((getter) => [getter.replace(ORG_APP_PREFIX, ''), computed(() => store.getters[getter])]),
  ) as unknown as MappedOrgAppGetters;
}

function mapMutations() {
  const store = getStore();
  return Object.fromEntries(
    Object.keys(store._mutations).map((mutation) => [mutation, (value) => store.commit(mutation, value)]),
  );
}

type MappedActions = {
  [K in keyof StoreInternals['_actions']]: MappedAction<StoreInternals['_actions'][K]>;
};

function mapActions(): MappedActions {
  const store = getStore();
  return Object.fromEntries(
    Object.keys(store._actions).map((action) => [action, (value) => store.dispatch(action, value)]),
  ) as unknown as MappedActions;
}

type MappedOrgAppActions = {
  [K in keyof OrgAppActions]: MappedAction<OrgAppActions[K]>;
};

function mapOrgAppActions(): MappedOrgAppActions {
  const store = getStore();
  return Object.fromEntries(
    Object.keys(store._actions)
      .filter((x) => x.startsWith(ORG_APP_PREFIX))
      .map((action) => [action.replace(ORG_APP_PREFIX, ''), (value) => store.dispatch(action, value)]),
  ) as unknown as MappedOrgAppActions;
}

type MappedExchAppActions = {
  [K in keyof ExchAppActions]: MappedAction<ExchAppActions[K]>;
};

function mapExchAppActions(): MappedExchAppActions {
  const store = getStore();
  return Object.fromEntries(
    Object.keys(store._actions)
      .filter((x) => x.startsWith(EXCH_APP_PREFIX))
      .map((action) => [action.replace(EXCH_APP_PREFIX, ''), (value) => store.dispatch(action, value)]),
  ) as unknown as MappedExchAppActions;
}

export {
  mapActions,
  mapExchAppActions,
  mapExchAppState,
  mapGetters,
  mapMutations,
  mapOrgAppActions,
  mapOrgAppGetters,
  mapOrgAppState,
  mapState,
};
